"use client";

import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
  KeyboardEvent,
} from "react";
import styles from "./SearchAutocomplete.module.css";

const MAX_RESULTS = 10;

export interface SearchAutocompleteItem {
  name: string;
  route: string;
}

export type SearchAutocompleteItemBase = { name: string };

export interface SearchAutocompleteProps<T extends SearchAutocompleteItemBase = SearchAutocompleteItem> {
  items: T[];
  placeholder?: string;
  /** Accessible name for the search input (e.g. for screen readers). Defaults to placeholder. */
  inputAriaLabel?: string;
  /** Accessible name for the results listbox. Defaults to "Search results". */
  listAriaLabel?: string;
  onSelect?: (item: T) => void;
  autoFocus?: boolean;
  formatResult?: (item: T) => React.ReactNode;
  maxResults?: number;
  showItemsOnFocus?: boolean;
  onSearch?: (query: string) => void;
}

export function SearchAutocomplete<T extends SearchAutocompleteItemBase>({
  items = [],
  placeholder = "Search...",
  inputAriaLabel,
  listAriaLabel = "Search results",
  onSelect,
  autoFocus = false,
  formatResult,
  maxResults = MAX_RESULTS,
  showItemsOnFocus = true,
  onSearch,
}: SearchAutocompleteProps<T>) {
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const filteredItems = useMemo(() => {
    if (!query.trim()) return items.slice(0, maxResults);
    const lower = query.toLowerCase();
    return items
      .filter((item) => item.name?.toLowerCase().includes(lower))
      .slice(0, maxResults);
  }, [items, query, maxResults]);

  const showDropdown = isOpen && (showItemsOnFocus || query.length > 0);

  const closeDropdown = useCallback(() => {
    setIsOpen(false);
    setHighlightedIndex(-1);
  }, []);

  const selectItem = useCallback(
    (item: T) => {
      onSelect?.(item);
      setQuery("");
      closeDropdown();
      inputRef.current?.focus();
    },
    [onSelect, closeDropdown]
  );

  useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
  }, [autoFocus]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        closeDropdown();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [closeDropdown]);

  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const el = listRef.current.children[highlightedIndex] as HTMLElement;
      el?.scrollIntoView({ block: "nearest" });
    }
  }, [highlightedIndex]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);
    setIsOpen(true);
    setHighlightedIndex(-1);
    onSearch?.(value);
  };

  const handleInputFocus = () => {
    setIsOpen(true);
    setHighlightedIndex(-1);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!showDropdown) {
      if (e.key === "Escape") (e.target as HTMLInputElement).blur();
      return;
    }
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((i) =>
          i < filteredItems.length - 1 ? i + 1 : i
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((i) => (i > 0 ? i - 1 : -1));
        break;
      case "Enter":
        e.preventDefault();
        if (highlightedIndex >= 0 && filteredItems[highlightedIndex]) {
          selectItem(filteredItems[highlightedIndex]);
        }
        break;
      case "Escape":
        e.preventDefault();
        closeDropdown();
        setHighlightedIndex(-1);
        break;
      default:
        break;
    }
  };

  const defaultFormatResult = (item: T) => (
    <span className={styles.resultItemText}>{item.name}</span>
  );

  const renderItem = formatResult ?? defaultFormatResult;

  return (
    <div ref={containerRef} className={styles.wrapper}>
      <input
        ref={inputRef}
        type="text"
        value={query}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className={styles.input}
        autoComplete="off"
        aria-autocomplete="list"
        aria-expanded={showDropdown}
        aria-controls="search-autocomplete-list"
        aria-label={inputAriaLabel ?? placeholder}
        aria-activedescendant={
          highlightedIndex >= 0
            ? `search-autocomplete-option-${highlightedIndex}`
            : undefined
        }
      />
      {showDropdown && (
        <ul
          ref={listRef}
          id="search-autocomplete-list"
          role="listbox"
          aria-label={listAriaLabel}
          className={styles.list}
        >
          {filteredItems.length === 0 ? (
            <li className={styles.noResults} role="option">
              No results found
            </li>
          ) : (
            filteredItems.map((item, index) => (
              <li
                key={`${(item as { name: string }).name}-${index}`}
                id={`search-autocomplete-option-${index}`}
                role="option"
                aria-selected={index === highlightedIndex}
                className={
                  index === highlightedIndex
                    ? `${styles.item} ${styles.itemHighlighted}`
                    : styles.item
                }
                onMouseEnter={() => setHighlightedIndex(index)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  selectItem(item);
                }}
              >
                {renderItem(item)}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
